import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  PoundSterling, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Receipt,
  CreditCard,
  RefreshCw,
  Search,
  Filter,
  Download,
  ChevronRight,
  Eye,
  BarChart3,
  Wallet,
  Target,
  CalendarClock
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FinanceDashboard() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [filters, setFilters] = useState({ status: '', dateRange: '30' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, invoicesRes, exceptionsRes, quotesRes] = await Promise.all([
        axios.get(`${API}/api/finance/dashboard`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/api/phs/members/invoices/list?limit=50`, { headers }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${API}/api/finance/exceptions`, { headers }).catch(() => ({ data: { exceptions: [] } })),
        axios.get(`${API}/api/phs/members/quotes/list?limit=50`, { headers }).catch(() => ({ data: { quotes: [] } }))
      ]);
      
      setSummary(summaryRes.data);
      setInvoices(invoicesRes.data?.invoices || []);
      setExceptions(exceptionsRes.data?.exceptions || []);
      setQuotes(quotesRes.data?.quotes || []);
    } catch (error) {
      console.error('Error loading finance data:', error);
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getStatusBadge = (status) => {
    const configs = {
      paid: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      pending: { color: 'bg-amber-100 text-amber-700', icon: Clock },
      overdue: { color: 'bg-red-100 text-red-700', icon: AlertCircle },
      draft: { color: 'bg-gray-100 text-gray-600', icon: FileText },
      sent: { color: 'bg-blue-100 text-blue-700', icon: FileText },
      accepted: { color: 'bg-teal-100 text-teal-700', icon: CheckCircle },
      declined: { color: 'bg-red-100 text-red-700', icon: AlertCircle },
      cancelled: { color: 'bg-gray-100 text-gray-600', icon: AlertCircle },
    };
    const config = configs[status] || { color: 'bg-gray-100 text-gray-600', icon: FileText };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Calculate stats from data
  const stats = {
    totalRevenue: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.client_total || 0), 0),
    awaitingPayment: invoices.filter(i => i.status === 'pending').reduce((sum, i) => sum + (i.client_total || 0), 0),
    grossMargin: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.phs_margin_total || 0), 0),
    contractorCosts: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.contractor_total || 0), 0),
    overdueCount: invoices.filter(i => i.status === 'overdue').length,
    pendingInvoices: invoices.filter(i => i.status === 'pending').length,
    acceptedQuotes: quotes.filter(q => q.status === 'accepted').length,
    pendingQuotes: quotes.filter(q => q.status === 'sent').length,
    exceptionCount: exceptions.length,
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="h-8 w-8 animate-spin text-[#0F766E]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6" data-testid="finance-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <PoundSterling className="h-6 w-6 text-[#0F766E]" />
              Finance Control
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Revenue tracking, invoices, and financial exceptions
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">Revenue</span>
              </div>
              <div className="text-2xl font-bold text-emerald-800">{formatCurrency(stats.totalRevenue)}</div>
              <div className="text-xs text-emerald-600 mt-1">Paid invoices</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-5 w-5 text-amber-600" />
                <span className="text-sm font-medium text-amber-700">Awaiting</span>
              </div>
              <div className="text-2xl font-bold text-amber-800">{formatCurrency(stats.awaitingPayment)}</div>
              <div className="text-xs text-amber-600 mt-1">{stats.pendingInvoices} invoices</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-teal-50 to-white border-teal-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-5 w-5 text-teal-600" />
                <span className="text-sm font-medium text-teal-700">Margin</span>
              </div>
              <div className="text-2xl font-bold text-teal-800">{formatCurrency(stats.grossMargin)}</div>
              <div className="text-xs text-teal-600 mt-1">25% PHS margin</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Contractor</span>
              </div>
              <div className="text-2xl font-bold text-blue-800">{formatCurrency(stats.contractorCosts)}</div>
              <div className="text-xs text-blue-600 mt-1">Costs paid</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-700">Quotes</span>
              </div>
              <div className="text-2xl font-bold text-purple-800">{stats.pendingQuotes}</div>
              <div className="text-xs text-purple-600 mt-1">Awaiting response</div>
            </CardContent>
          </Card>

          <Card className={`bg-gradient-to-br ${stats.exceptionCount > 0 ? 'from-red-50 to-white border-red-200' : 'from-gray-50 to-white border-gray-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className={`h-5 w-5 ${stats.exceptionCount > 0 ? 'text-red-600' : 'text-gray-500'}`} />
                <span className={`text-sm font-medium ${stats.exceptionCount > 0 ? 'text-red-700' : 'text-gray-600'}`}>Exceptions</span>
              </div>
              <div className={`text-2xl font-bold ${stats.exceptionCount > 0 ? 'text-red-800' : 'text-gray-700'}`}>{stats.exceptionCount}</div>
              <div className={`text-xs ${stats.exceptionCount > 0 ? 'text-red-600' : 'text-gray-500'} mt-1`}>Need attention</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="quotes">Quotes</TabsTrigger>
            <TabsTrigger value="exceptions">Exceptions</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Awaiting Payment */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-600" />
                    Awaiting Payment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {invoices.filter(i => i.status === 'pending').slice(0, 5).map((invoice) => (
                    <div key={invoice.invoice_number} className="flex items-center justify-between py-3 border-b last:border-0">
                      <div>
                        <div className="font-mono text-sm font-medium">{invoice.invoice_number}</div>
                        <div className="text-xs text-gray-500">{invoice.member_name}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-amber-700">{formatCurrency(invoice.client_total)}</div>
                        <div className="text-xs text-gray-500">Due {formatDate(invoice.due_date)}</div>
                      </div>
                    </div>
                  ))}
                  {invoices.filter(i => i.status === 'pending').length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-6">No invoices awaiting payment</p>
                  )}
                </CardContent>
              </Card>

              {/* Accepted Quotes Awaiting Payment */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-600" />
                    Accepted Quotes (Unpaid)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {quotes.filter(q => q.status === 'accepted').slice(0, 5).map((quote) => (
                    <div key={quote.quote_id} className="flex items-center justify-between py-3 border-b last:border-0">
                      <div>
                        <div className="font-mono text-sm font-medium">{quote.quote_id}</div>
                        <div className="text-xs text-gray-500">{quote.member_name}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-teal-700">{formatCurrency(quote.client_total)}</div>
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Awaiting Payment</Badge>
                      </div>
                    </div>
                  ))}
                  {quotes.filter(q => q.status === 'accepted').length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-6">No accepted quotes awaiting payment</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Finance Exceptions */}
            {exceptions.length > 0 && (
              <Card className="border-red-200">
                <CardHeader className="pb-2 bg-red-50">
                  <CardTitle className="text-base flex items-center gap-2 text-red-800">
                    <AlertTriangle className="h-4 w-4" />
                    Finance Exceptions Requiring Attention
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {exceptions.slice(0, 5).map((exc, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-red-800">{exc.type}</div>
                          <div className="text-sm text-red-600">{exc.description}</div>
                          <div className="text-xs text-red-500 mt-1">
                            {exc.entity_type}: {exc.entity_id}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-100">
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">All Invoices</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input placeholder="Search invoices..." className="pl-10 w-48" />
                    </div>
                    <Select value={filters.status || "all"} onValueChange={(v) => setFilters({...filters, status: v === "all" ? "" : v})}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Member</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Margin</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices
                      .filter(i => !filters.status || i.status === filters.status)
                      .map((invoice) => (
                      <TableRow key={invoice.invoice_number}>
                        <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                        <TableCell>
                          <div className="font-medium">{invoice.member_name}</div>
                          <div className="text-xs text-gray-500">{invoice.task_id}</div>
                        </TableCell>
                        <TableCell className="font-semibold">{formatCurrency(invoice.client_total)}</TableCell>
                        <TableCell className="text-teal-600">{formatCurrency(invoice.phs_margin_total)}</TableCell>
                        <TableCell>{formatDate(invoice.due_date)}</TableCell>
                        <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {invoices.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No invoices found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">All Quotes</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input placeholder="Search quotes..." className="pl-10 w-48" />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quote #</TableHead>
                      <TableHead>Member</TableHead>
                      <TableHead>Contractor Cost</TableHead>
                      <TableHead>Client Total</TableHead>
                      <TableHead>Margin</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotes.map((quote) => (
                      <TableRow key={quote.quote_id}>
                        <TableCell className="font-mono text-sm">{quote.quote_id}</TableCell>
                        <TableCell>
                          <div className="font-medium">{quote.member_name}</div>
                          <div className="text-xs text-gray-500">{quote.task_id}</div>
                        </TableCell>
                        <TableCell className="text-blue-600">{formatCurrency(quote.contractor_total)}</TableCell>
                        <TableCell className="font-semibold">{formatCurrency(quote.client_total)}</TableCell>
                        <TableCell className="text-teal-600">{formatCurrency(quote.phs_margin_total)}</TableCell>
                        <TableCell>{getStatusBadge(quote.status)}</TableCell>
                        <TableCell>{formatDate(quote.sent_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {quotes.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No quotes found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Exceptions Tab */}
          <TabsContent value="exceptions" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  Finance Exceptions
                </CardTitle>
                <CardDescription>Issues requiring manual review or resolution</CardDescription>
              </CardHeader>
              <CardContent>
                {exceptions.length > 0 ? (
                  <div className="space-y-3">
                    {exceptions.map((exc, idx) => (
                      <div key={idx} className="flex items-start gap-4 p-4 bg-red-50 rounded-lg border border-red-200">
                        <AlertCircle className="h-6 w-6 text-red-600 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-red-800">{exc.type}</span>
                            <Badge variant="outline" className="border-red-300 text-red-600">{exc.severity || 'warning'}</Badge>
                          </div>
                          <p className="text-sm text-red-700">{exc.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-red-600">
                            <span>{exc.entity_type}: <span className="font-mono">{exc.entity_id}</span></span>
                            <span>{formatDate(exc.created_at)}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-100">
                            Resolve
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                    <p className="text-green-700 font-medium">No exceptions</p>
                    <p className="text-sm text-gray-500">All financial records are in order</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
